import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import Icon from '../Icon.jsx';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import PageSkeleton from '../Skeleton.jsx';

// Sıra pakete göre artan yetenek sırası — tabloyu okurken yükseltmenin ne
// getireceği soldan sağa okunabilsin.
const FEATURE_KEYS = [
  'qr_ordering', 'table_management', 'open_table', 'online_payments',
  'split_payments', 'kitchen_printing', 'multi_printer', 'loyalty',
  'analytics_basic', 'analytics_advanced', 'waiter_call', 'ai_assistant', 'max_tables',
];

const pct = (bps) => `${(bps / 100).toFixed(2)}%`;

export default function Overview() {
  const t = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api('/api/restaurant').then(setData).catch((e) => setErr(errMessage(t, e)));
    // eslint-disable-next-line
  }, []);

  if (err) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="form" />;

  const r = data.restaurant;
  const trialLeft = r.trialEndsAt ? Math.ceil((new Date(r.trialEndsAt) - Date.now()) / 864e5) : null;

  return (
    <>
      <div className="card">
        <h2>{r.name}</h2>
        <div className="grid2">
          <Field k={t('ov.slug')} v={r.slug} />
          <Field k={t('ov.countryCurrency')} v={`${r.country} · ${r.currency}`} />
          <Field k={t('ov.timezone')} v={r.timezone} />
          <Field k={t('ov.vatDefault')} v={pct(r.vatRateDefault)} />
          <Field k={t('ov.plan')} v={r.planKey || '—'} />
          <Field k={t('ov.subscription')} v={
            <span className={`badge ${r.subscriptionStatus === 'active' ? 'ok' : r.subscriptionStatus === 'trialing' ? 'warn' : 'bad'}`}>
              {r.subscriptionStatus || t('none')}
            </span>
          } />
          <Field k={t('ov.orderingMode')} v={data.settings?.orderingMode === 'OPEN_TABLE' ? t('ov.modeOpen') : t('ov.modePayFirst')} />
          <Field k={t('ov.stripePayouts')} v={
            <span className={`badge ${r.connectStatus === 'COMPLETED' ? 'ok' : 'warn'}`}>{r.connectStatus}</span>
          } />
        </div>
        {data.pricing && (
          <p className="muted" style={{ marginBottom: 6 }}>
            <b>{t('ov.pricing')}</b>{' '}
            {data.pricing.model === 'COMMISSION'
              ? <>
                  {t('ov.pricingCommission', { rate: pct(data.pricing.commissionBps) })}
                  {data.pricing.commissionNegotiated &&
                    <span className="badge ok" style={{ marginLeft: 6 }}>{t('ov.negotiatedRate')}</span>}
                </>
              : t('ov.pricingFlat')}
          </p>
        )}
        {trialLeft != null && trialLeft > 0 && (
          <p className="muted" style={{ marginBottom: 0 }}>{t('ov.trialLeft', { n: trialLeft })}</p>
        )}
      </div>

      <div className="card">
        <h2>{t('ov.planFeatures')}</h2>
        <table>
          <tbody>
            {FEATURE_KEYS.map((k) => {
              const f = data.features[k];
              const on = !!f?.enabled;
              // Masa sınırı "kapalı" değil, SAYI: üst paketle artar, bu yüzden
              // sınırlıysa o da yükseltme çağrısı taşır.
              const limited = k === 'max_tables' && on && f.limitInt != null;
              return (
                <tr key={k}>
                  <td>{t(`feat.${k}`)}</td>
                  <td style={{ width: 160 }}>
                    {k === 'max_tables'
                      ? (on ? (f.limitInt == null ? t('unlimited') : t('ov.tablesLimit', { n: f.limitInt })) : '—')
                      : <span className={`badge ${on ? 'ok' : ''}`}>{on ? t('on') : t('off')}</span>}
                  </td>
                  {/* Kapalı özelliğin yanında yükseltme kapısı: "neden yok"
                      sorusunun cevabı bir tık ötede dursun. */}
                  <td style={{ width: 150 }}>
                    {(!on || limited) && (
                      <Link to="/settings/billing">
                        <button className="btn-sm"><Icon n="star" />{t('nav.upgrade')}</button>
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="muted" style={{ marginBottom: 0 }}>{t('ov.featuresNote')}</p>
      </div>
    </>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <label>{k}</label>
      <div>{v}</div>
    </div>
  );
}
